import { useEffect, useMemo, useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { GripVertical, Loader2, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";

const SOURCE = { manual: "Set by admin", nba: "NBA.com expected", rotowire: "RotoWire expected", espn: "ESPN confirmed" };

function Row({ p, index, locked }) {
  const controls = useDragControls();
  const starter = index < 5;
  return (
    <Reorder.Item value={p.id} dragListener={false} dragControls={controls} className={`ra-five-row ${starter ? "is-starter" : ""}`} data-testid={`starter-row-${p.id}`}>
      {index === 5 && <span className="ra-five-divider">BENCH</span>}
      <button type="button" className="ra-five-grip" disabled={locked} onPointerDown={(e) => !locked && controls.start(e)} aria-label={`Drag ${p.name}`} data-testid={`starter-grip-${p.id}`}>
        <GripVertical size={16} />
      </button>
      <span className="ra-five-slot">{starter ? index + 1 : ""}</span>
      {p.photo ? <img src={p.photo} alt="" /> : <i />}
      <span className="ra-five-name"><strong>{p.name}</strong><small>{[p.position, `${p.avg_min} MIN/G`].filter(Boolean).join(" · ")}</small></span>
      <em className={starter ? "st" : "bn"}>{starter ? "STARTER" : "BENCH"}</em>
    </Reorder.Item>
  );
}

export function StartersEditor({ ctx, outIds, setCtx }) {
  const initial = useMemo(() => {
    const avail = ctx.players.filter((p) => !outIds.includes(p.id));
    const first = ctx.starters.filter((id) => avail.some((p) => p.id === id));
    return [...first, ...avail.filter((p) => !first.includes(p.id)).map((p) => p.id)];
  }, [ctx, outIds]);
  const [order, setOrder] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => setOrder(initial), [initial]);
  const byId = useMemo(() => Object.fromEntries(ctx.players.map((p) => [p.id, p])), [ctx]);
  const locked = !ctx.starters_editable;
  const dirty = order.slice(0, 5).join() !== ctx.starters.slice(0, 5).join();

  const call = async (req, msg) => {
    setBusy(true);
    try { const r = await req(); setCtx(r.data); toast.success(msg); } catch (e) { toast.error(errorText(e)); }
    setBusy(false);
  };
  const save = () => call(() => api.put("/rotation/starters", { game_id: ctx.game_id, team_id: ctx.team_id, player_ids: order.slice(0, 5) }),
    "Starting five saved — projections updated");
  const reset = () => call(() => api.delete("/rotation/starters", { params: { game_id: ctx.game_id, team_id: ctx.team_id } }), "Back to the feed's starting five");

  return (
    <section className="ra-five" data-testid="starters-editor">
      <header>
        <div>
          <h3>STARTING FIVE · {ctx.team_code}</h3>
          <p data-testid="starters-source">
            {locked ? (ctx.starters_confirmed ? "Confirmed by ESPN — locked." : "Game already started — locked.")
              : `Drag players; the top 5 start. Current: ${SOURCE[ctx.starters_source] || "no expected five yet"}. Saving updates the official projections until ESPN confirms.`}
          </p>
        </div>
        {!locked && (
          <div className="ra-five-actions">
            {ctx.starters_source === "manual" && (
              <button type="button" onClick={reset} disabled={busy} className="ra-five-btn ghost" data-testid="starters-reset"><RotateCcw size={14} />Use feed</button>
            )}
            <button type="button" onClick={save} disabled={busy || !dirty || order.length < 5} className="ra-five-btn" data-testid="starters-save">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}Save five
            </button>
          </div>
        )}
      </header>
      <Reorder.Group axis="y" values={order} onReorder={locked ? () => {} : setOrder} className="ra-five-list" data-testid="starters-list">
        {order.map((id, i) => byId[id] && <Row key={id} p={byId[id]} index={i} locked={locked} />)}
      </Reorder.Group>
    </section>
  );
}
